/**
 * @file Sidebar 侧栏导航
 * @description 后台左侧壳；内部用 Menu 渲染导航项。展开宽 256px（w-64）。
 *
 * @whenToUse Admin Layout 左侧导航。
 * @whenNotToUse 页内锚点目录（用 Tree）；操作浮层（用 DropdownMenu）。
 */

import type { ReactNode } from 'react'
import { cn } from '@/lib/cn.ts'
import { Menu, type MenuItem } from '@/components/ui/menu.tsx'

export type SidebarItem = {
  key: string
  label: ReactNode
  to?: string
  icon?: ReactNode
  children?: SidebarItem[]
  disabled?: boolean
}

export type SidebarProps = {
  items: SidebarItem[]
  /**
   * 品牌区内容；可传入节点，或按折叠态渲染（收起时通常只留 logo）。
   */
  brand?: ReactNode | ((collapsed: boolean) => ReactNode)
  /** 是否折叠为仅图标（传给 Menu.collapsed） */
  collapsed?: boolean
  /** 折叠变化（透传 Menu.onCollapsedChange） */
  onCollapsedChange?: (collapsed: boolean) => void
  /** 底部区域 */
  footer?: ReactNode
  className?: string
}

function toMenuItems(items: SidebarItem[]): MenuItem[] {
  return items.map((item) => ({
    key: item.key,
    label: item.label,
    to: item.to,
    icon: item.icon,
    disabled: item.disabled,
    type: item.children?.length ? 'group' : 'item',
    children: item.children ? toMenuItems(item.children) : undefined,
  }))
}

export function Sidebar({
  items,
  brand,
  collapsed = false,
  onCollapsedChange,
  footer,
  className,
}: SidebarProps) {
  const brandNode = typeof brand === 'function' ? brand(collapsed) : brand

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border bg-surface transition-[width] duration-300',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
    >
      {brandNode ? (
        <div
          className={cn(
            'flex h-14 shrink-0 items-center border-b border-border px-3',
            collapsed && 'justify-center px-2',
          )}
        >
          {brandNode}
        </div>
      ) : null}
      <nav className="flex-1 overflow-y-auto p-2">
        <Menu
          mode="vertical"
          items={toMenuItems(items)}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
        />
      </nav>
      {footer ? (
        <div className="shrink-0 border-t border-border p-2">{footer}</div>
      ) : null}
    </aside>
  )
}
